import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';
import { ActionIcon, Badge, Button, Menu, Skeleton, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { RunStatus } from '@prisma/client';
import { IconDotsVertical, IconExternalLink, IconReload, IconStrikethrough } from '@tabler/icons-react';
import Link from 'next/link';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useTestReRun } from '~/hooks/test/useTestReRun';
import { api } from '~/trpc-extra/api';
import { useMemo } from 'react';
import { GitBranchBadge } from '../common/GitBranchBadge';
import StatusIcon from '../icons/StatusIcon';
import TestRunAttemptIssueSnippet from './TestRunAttemptIssueSnippet';
import AutohealSuggestionQuickView from '~/components/history/AutohealSuggestionQuickView';
import type { TestRunExecutionOutput } from '~/types';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { openViaForm } from '~/logic/url/open-via-form';

const TEST_RUN_STATUS_MANTINE_COLORS: Record<RunStatus, string> = {
  PASSED: 'green',
  FLAKY: 'green',
  FAILED: 'red',
  RUNNING: 'yellow',
  RETRYING: 'yellow',
  ERROR: 'yellow',
  SKIPPED: 'gray',
  QUEUED: 'gray',
  CANCELLED: 'gray',
};

export default function TestRunHeader({ runId }: { runId: string }) {
  const trpcUtils = api.useUtils();
  const projectId = useProjectId();
  const [animationParent] = useAutoAnimate();
  const { isAdmin } = useAuthData();

  const { data: testRun, isLoading } = api.testRun.get.useQuery({ runId });
  const { data: suiteRunStatus } = api.testRun.getSuiteRunStatus.useQuery({ runId });
  const { mutateAsync: getLangSmithTraceUrl } = api.admin.getLangSmithTraceUrl.useMutation();
  const { data: suiteRun } = api.suiteRun.get.useQuery(
    { runId: testRun?.testSuiteRun?.id! },
    { enabled: !!testRun?.testSuiteRun?.id },
  );

  const { mutateAsync: createDraftForTestAndOpenInTab, isPending: isCreatingDraft } = api.testContent.fork.useMutation({
    onMutate: () => {
      notifications.show({ message: 'Creating a new draft copy...', color: 'gray' });
    },
    onSuccess: (testDraft) => {
      openViaForm(`/project/${projectId}/testDraft/${testDraft.id}?fromTest=true`, '_blank');
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Failed to create test draft', color: 'red' });
    },
  });

  // Get test suite data to check if test is already skipped
  const { data: testSuite } = api.testSuite.get.useQuery(
    {
      projectId: projectId!,
      testSuiteId: testRun?.testSuiteRun?.testSuite.id || '',
    },
    {
      enabled: !!projectId && !!testRun?.testSuiteRun?.testSuite.id,
    },
  );

  const { handleReRun, isReRunning } = useTestReRun({
    // We only show the warning if the suite is still running and if the suite run is not set to run in parallel
    showWarning: suiteRunStatus?.status === 'RUNNING' && (suiteRun?.numberOfWorkers ?? 1) === 1,
  });

  const isTestSkipped = useMemo(() => {
    if (!testSuite?.suiteTests || !testRun) {
      return false;
    }
    const suiteTest = testSuite.suiteTests.find((st) => st.testId === testRun.test.id);
    return suiteTest?.skip ?? false;
  }, [testSuite, testRun]);

  const { mutate: updateTests } = api.testSuite.updateTests.useMutation({
    onSuccess: async () => {
      notifications.show({
        message: 'Test updated',
        color: 'green',
      });
      trpcUtils.testSuite.get.invalidate().catch(() => undefined);
      trpcUtils.testSuite.getRuns.invalidate().catch(() => undefined);
      trpcUtils.testRun.get.invalidate().catch(() => undefined);
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update the test',
        color: 'red',
      });
    },
  });

  const handleToggleSkip = () => {
    if (!testRun?.testSuiteRun?.testSuite.id) {
      return;
    }

    const operation = isTestSkipped ? 'unskip' : 'skip';

    updateTests({
      projectId: projectId!,
      testSuiteId: testRun.testSuiteRun.testSuite.id,
      testIds: [testRun.test.id],
      operation,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-start justify-between space-x-2 border-b-orange-100">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Skeleton
              height={30}
              circle
            />
            <Skeleton
              height={28}
              width={200}
            />
          </div>
          <div className="mt-2 pl-[39px]">
            <Skeleton
              height={16}
              width={300}
            />
          </div>
        </div>
        <div className="flex items-center space-x-4 pr-8">
          <Skeleton
            height={36}
            width={90}
          />
          <Skeleton
            height={36}
            width={90}
          />
        </div>
      </div>
    );
  }

  if (!testRun) {
    return null;
  }

  // Type guard to safely check execution output
  const isExecutionOutput = (value: unknown): value is TestRunExecutionOutput =>
    !!value && typeof value === 'object' && !Array.isArray(value);

  // testRun.TestRunAttempts is in reverse chronological order
  const lastAttempt = testRun.TestRunAttempts?.at(0);
  const lastAttemptExecutionOutput = lastAttempt?.executionOutput;
  const autohealSuggestion =
    isExecutionOutput(lastAttemptExecutionOutput) ? lastAttemptExecutionOutput.autohealSuggestion : undefined;

  const firstStatusHistory = testRun.statusHistory?.at(0);
  const lastStatusHistory = testRun.statusHistory?.at(-1);

  return (
    <>
      <div className="flex items-start justify-between space-x-2 border-b-orange-100">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <StatusIcon
              status={testRun.status}
              size={24}
              filled={testRun.status !== 'FLAKY'}
              statusHistory={testRun.statusHistory}
            />
            <Title
              className="flex flex-row items-center gap-1"
              order={3}
            >
              <Link
                href={`/project/${projectId}/test/${testRun.test.id}`}
                target="_blank"
                className="font-medium text-gray-800 transition-colors hover:underline dark:text-stone-200"
              >
                {testRun?.test.name}
              </Link>
              <span className="ml-1 font-normal text-gray-600 dark:text-stone-400">#{runId.slice(0, 8)}</span>
            </Title>
          </div>

          <span className="pl-[39px]">
            {testRun.status === 'ERROR' && lastAttempt?.AttemptError?.message}
            {testRun.status === 'ERROR' && lastAttempt?.AttemptError?.stepTitle ?
              ` @ step "${lastAttempt.AttemptError?.stepTitle}"`
            : ''}
          </span>
        </div>

        <div className="flex items-center space-x-4 pr-8">
          <div className="flex flex-row items-center gap-2">
            <Tooltip label="Rerun the test with the latest version, creating a new attempt">
              <Button
                variant="default"
                leftSection={<IconReload size={16} />}
                disabled={
                  isReRunning ||
                  testRun.status === 'RUNNING' ||
                  testRun.status === 'SKIPPED' ||
                  testRun.status === 'QUEUED' ||
                  testRun.status === 'RETRYING'
                }
                onClick={() => handleReRun([runId])}
              >
                Rerun
              </Button>
            </Tooltip>

            {/* Hidden: Change Status button */}
            {/* {canOverrideStatus ?
              <Button
                variant="default"
                leftSection={<IconPencilCheck size={16} />}
                onClick={() => {
                  modals.open({
                    title: 'Change Status',
                    zIndex: 310,
                    children: (
                      <OverrideStatusForm
                        runId={runId}
                        currentStatus={testRun.status}
                        currentReason={lastStatusHistory?.note || undefined}
                        onSuccess={() => {
                          modals.closeAll();
                        }}
                      />
                    ),
                  });
                }}
              >
                Change Status
              </Button>
            : null} */}
            {lastAttempt?.id ?
              <TestRunAttemptIssueSnippet
                runId={runId}
                attemptId={lastAttempt.id}
              />
            : null}
            <Button
              title="Edit Original Test"
              loading={isCreatingDraft}
              onClick={() =>
                createDraftForTestAndOpenInTab(
                  // testRun.TestRunAttempts.at(0) is the latest test run attempt
                  // This ensures we are editing the latest test version
                  testRun.TestRunAttempts.at(0)?.inputTestContentId ?
                    {
                      testContentId: testRun.TestRunAttempts.at(0)?.inputTestContentId ?? '',
                      gitBranch: testRun.testSuiteRun?.gitBranch ?? undefined,
                    }
                    // test in suite guarantees trunkContentId
                  : { testContentId: testRun.test.trunkContentId! },
                )
              }
              variant="outline"
            >
              Edit Original Test
            </Button>

            {/* Column 4: Action Menu */}
            <div className="col-span-1 flex items-center justify-end">
              <Menu
                width={200}
                shadow="md"
                position="bottom-end"
              >
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                  >
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconExternalLink size={16} />}
                    target="_blank"
                    component={Link}
                    href={`/project/${projectId}/test/${testRun.test.id}`}
                  >
                    View Test
                  </Menu.Item>
                  {!!isAdmin && (
                    <Menu.Item
                      leftSection={
                        <IconExternalLink
                          size={16}
                          className="text-orange-500"
                        />
                      }
                      onClick={async () => {
                        const traceUrl = await getLangSmithTraceUrl({
                          testRunAttemptId: lastAttempt?.id ?? '',
                        });
                        window.open(traceUrl, '_blank');
                      }}
                      className="text-orange-500"
                    >
                      View LangSmith Trace
                    </Menu.Item>
                  )}
                  {!!testRun.testSuiteRun?.testSuite.id && (
                    <Tooltip
                      label={
                        testRun.status === 'RUNNING' ? 'Cannot skip while test is running'
                        : testRun.status === 'QUEUED' ?
                          'Cannot skip while test is queued'
                        : undefined
                      }
                      disabled={testRun.status !== 'RUNNING' && testRun.status !== 'QUEUED'}
                    >
                      <Menu.Item
                        leftSection={<IconStrikethrough size={16} />}
                        onClick={handleToggleSkip}
                        disabled={testRun.status === 'RUNNING' || testRun.status === 'QUEUED'}
                      >
                        {isTestSkipped ? 'Unskip' : 'Skip'}
                      </Menu.Item>
                    </Tooltip>
                  )}
                </Menu.Dropdown>
              </Menu>
            </div>
          </div>
        </div>
      </div>
      <div ref={animationParent}>
        {!!firstStatusHistory?.status &&
          !!lastStatusHistory?.status &&
          firstStatusHistory.status !== testRun.status && (
            <div className="mt-2 flex items-center gap-2 text-sm text-stone-600 dark:text-stone-400">
              <Badge
                color={TEST_RUN_STATUS_MANTINE_COLORS[firstStatusHistory.status]}
                variant="light"
                radius="sm"
              >
                {firstStatusHistory.status}
              </Badge>
              →
              <Badge
                color={TEST_RUN_STATUS_MANTINE_COLORS[testRun.status]}
                variant="light"
                radius="sm"
              >
                {testRun.status}
              </Badge>
              {!!lastStatusHistory.note && (
                <span className="text-gray-600 italic dark:text-gray-300">&ldquo;{lastStatusHistory.note}&rdquo;</span>
              )}
            </div>
          )}
      </div>
      <div className="mt-2 flex flex-row items-center gap-4 text-sm text-stone-600 dark:text-stone-400">
        <span>Started {testRun.createdAt.toLocaleString()}</span>
        <AutohealSuggestionQuickView
          suggestion={autohealSuggestion}
          latestAttempt={lastAttempt}
          suiteRunId={testRun.testSuiteRunId ?? undefined}
          testRunId={testRun.id}
          showLearnMoreLink={false}
          testStatus={testRun.status}
          isLatestSuiteRun={true}
        />
        {!!testRun.testSuiteRun?.gitBranch && (
          <GitBranchBadge
            size="md"
            gitBranch={testRun.testSuiteRun.gitBranch}
          />
        )}
        {!!isTestSkipped && (
          <Badge
            color="gray"
            variant="light"
            radius="sm"
            size="sm"
          >
            Skipped in Suite
          </Badge>
        )}
      </div>
    </>
  );
}
